import { RichText } from "@payloadcms/richtext-lexical/react";
import { useQuery } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { z } from "zod";

import { StandardContent } from "@/components/App";
import Header from "@/components/Header";
import { fallbackAutoRenewContent } from "@/constants/fallbackAutoRenewContent";
import { PageTitle } from "@/constants/pages";
import { usePageEventTracking } from "@/hooks/event-tracking";
import { fetchJSONFactory } from "@/utils/fetch-json";

const CMS_API = import.meta.env.VITE_CMS_API;
const fetchJSON = fetchJSONFactory(CMS_API);

export const legalContentSchema = z.object({
  autoRenew: z.object({
    title: z.string().min(1, { message: "Title is required" }),
    text: z.string().min(1, { message: "Body content is required" }),
    generalConditions: z.any(),
  }),
});

export type CMSLegalContent = z.infer<typeof legalContentSchema>;

export const CMSQueryKey = ["cms"];

export function useCMSLegalContent() {
  return useQuery({
    queryKey: [...CMSQueryKey, "legal-content"],
    queryFn: async (): Promise<CMSLegalContent> => {
      try {
        const response = await fetchJSON<CMSLegalContent>(
          "/globals/legal-content",
        );

        // Validate response with Zod
        const validatedContent = legalContentSchema.parse(response);
        return validatedContent;
      } catch (error) {
        // Fallback content if CMS is not available or validation fails
        return fallbackAutoRenewContent;
      }
    },
  });
}

export default function AboutAutoRenewPage() {
  usePageEventTracking();
  useDocumentTitle(PageTitle.ABOUT_AUTO_RENEW);

  const { data, isLoading, error, refetch } = useCMSLegalContent();

  if (isLoading) {
    return (
      <StandardContent>
        <Header className="mb-6">About Auto-Renew</Header>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <p className="text-gray-600">Loading auto-renewal information...</p>
          </div>
        </div>
      </StandardContent>
    );
  }

  if (error) {
    return (
      <StandardContent>
        <Header className="mb-6">About Auto-Renew</Header>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 text-xl text-red-600">⚠️</div>
            <h2 className="mb-2 text-lg font-semibold text-gray-900">
              Error loading content
            </h2>
            <p className="mb-4 text-gray-600">
              {error instanceof Error ? error.message : "Error loading content"}
            </p>
            <button
              onClick={() => void refetch()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Try again
            </button>
          </div>
        </div>
      </StandardContent>
    );
  }

  return (
    <StandardContent>
      <Header className="mb-6">About Auto-Renew</Header>

      {data?.autoRenew?.generalConditions && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-[#EEF6FA] p-6">
          <RichText
            data={data?.autoRenew?.generalConditions}
            converters={({ defaultConverters }) => ({
              ...defaultConverters,
              heading: ({ node, nodesToJSX }) => (
                <h2>{nodesToJSX({ nodes: node.children })}</h2>
              ),
              paragraph: ({ node, nodesToJSX }) => (
                <p>{nodesToJSX({ nodes: node.children })}</p>
              ),
            })}
          />
        </div>
      )}
    </StandardContent>
  );
}
